import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, Eye, Image, X, User, FileImage, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const [reports, setReports] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]); // Multi-select: array of user IDs
  const [users, setUsers] = useState([]); // List of petugas/agen
  const [isLoading, setIsLoading] = useState(false);
  const [viewingProof, setViewingProof] = useState(null);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [showMode, setShowMode] = useState("all"); // "all" = Tagihan + Admin, "tagihan_only" = Tagihan Saja
  const [showUserDropdown, setShowUserDropdown] = useState(false); // Toggle dropdown visibility
  const [activeTab, setActiveTab] = useState("digiflazz"); // "digiflazz" | "lainnya"
  const pdfExportRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch users list for filter dropdown
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only active users (petugas and agen)
      const activeUsers = response.data.filter(u => 
        u.status === 'active' && (u.role === 'petugas' || u.role === 'agen')
      );
      setUsers(activeUsers);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      setInitialLoad(false);
      const token = localStorage.getItem("admin_token");
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      // Send multi-select user_ids as comma-separated string
      if (selectedUsers.length > 0) {
        params.append("user_ids", selectedUsers.join(","));
      }
      
      const response = await axios.get(`${API}/admin/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat laporan (unauthorized).");
      } else {
        toast.error("Gagal memuat laporan");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle user selection for multi-select
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Get display text for selected users
  const getSelectedUsersText = () => {
    if (selectedUsers.length === 0) return "Semua";
    if (selectedUsers.length === 1) {
      const user = users.find(u => u.id === selectedUsers[0]);
      return user ? `${user.name || user.username}` : "1 dipilih";
    }
    return `${selectedUsers.length} dipilih`;
  };

  // Fetch users and summary on initial load
  useEffect(() => {
    fetchUsers();
    fetchReports();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // --- Tab separation: DigiFlazz vs Lainnya -------------------------------
  const monthCountPayment = (p) => {
    if (p.service_type !== 'pdam' && p.service_type !== 'pdam_tebo') return 0;
    if (p.jumlah_bulan && p.jumlah_bulan > 0) return p.jumlah_bulan;
    const pl = p.periode_list || "";
    const m = pl.split(',').filter((s) => s.trim()).length;
    return m || 1;
  };
  const allPayments = (reports && reports.payments) || [];
  const displayPayments = allPayments.filter((p) =>
    activeTab === "digiflazz" ? p.is_digiflazz : !p.is_digiflazz
  );
  const computeTabSummary = (list) => {
    const tagihan = list.reduce((s, p) => s + (p.jumlah || 0), 0);
    const admin = list.reduce((s, p) => s + (p.admin_fee || 0), 0);
    const amount = list.reduce((s, p) => s + (p.total_bayar || 0), 0);
    const bulan = list.reduce((s, p) => s + monthCountPayment(p), 0);
    return {
      total_transactions: list.length,
      total_amount: amount,
      total_tagihan: tagihan,
      total_admin_fee: admin,
      total_petugas: bulan * 1000,
      total_sistem: bulan * 1000,
      total_maintenance: bulan * 500,
    };
  };
  const displaySummary = reports
    ? (allPayments.length ? computeTabSummary(displayPayments) : reports.summary)
    : null;

  const exportCSV = () => {
    if (!reports || !displayPayments.length) {
      toast.error("Tidak ada data untuk di-export");
      return;
    }

    const formatRupiah = (amount) => {
      return `Rp ${new Intl.NumberFormat("id-ID").format(amount || 0)}`;
    };

    const isTagihanOnly = showMode === "tagihan_only";

    // Calculate totals
    const totalTagihan = displayPayments.reduce((sum, p) => sum + (p.jumlah || 0), 0);
    const totalAdmin = displaySummary.total_admin_fee || 0;
    const totalBayar = displaySummary.total_amount || 0;

    // Create worksheet data - conditionally include admin columns
    const modeLabel = isTagihanOnly ? " (Tagihan Saja)" : "";
    const headerRow = isTagihanOnly 
      ? ["No", "No Sambungan", "Bulan", "Nama Pelanggan", "Periode", "Tagihan", "Metode", "Kasir", "No WhatsApp", "Tgl Submit", "Tgl Konfirmasi", "Status"]
      : ["No", "No Sambungan", "Bulan", "Nama Pelanggan", "Periode", "Tagihan", "Biaya Admin", "Petugas", "Sistem", "Maint.", "Total Bayar", "Metode", "Kasir", "No WhatsApp", "Tgl Submit", "Tgl Konfirmasi", "Status"];
    
    const wsData = [
      [`LAPORAN PEMBAYARAN PDAM${modeLabel}`],
      [`Periode: ${startDate || 'Semua'} s/d ${endDate || 'Semua'}`],
      [`Tanggal Export: ${formatDateExcel(new Date().toISOString())}`],
      [], // Empty row
      headerRow,
    ];

    // Add data rows
    displayPayments.forEach((p, i) => {
      // Calculate total months - prefer jumlah_bulan from server
      const countMonths = () => {
        if (p.service_type !== 'pdam' && p.service_type !== 'pdam_tebo') return 0;
        // First try jumlah_bulan from server
        if (p.jumlah_bulan && p.jumlah_bulan > 0) {
          return p.jumlah_bulan;
        }
        // Fallback to parsing periode_list
        if (!p.periode_list) return 1;
        const months = p.periode_list.split(',').filter(m => m.trim()).length;
        return months || 1;
      };
      
      const bulanCount = countMonths();
      const isPDAM = p.service_type === 'pdam' || p.service_type === 'pdam_tebo';
      // Calculate breakdown per transaction
      const biayaPetugas = isPDAM ? bulanCount * 1000 : 0;
      const biayaSistem = isPDAM ? bulanCount * 1000 : 0;
      const biayaMaintenance = isPDAM ? bulanCount * 500 : 0;
      
      const baseRow = [
        i + 1,
        p.no_samb,
        isPDAM ? bulanCount : '-',
        p.nama,
        p.periode_list || p.bulan || '',
        formatRupiah(p.jumlah),
      ];
      
      if (isTagihanOnly) {
        // Tagihan only mode - skip admin columns
        wsData.push([
          ...baseRow,
          p.payment_method?.toLowerCase() === 'cash' ? 'Cash' : p.payment_method?.toLowerCase() === 'transfer' ? 'Transfer' : '-',
          p.cashier_name ? `${p.cashier_name} (${p.cashier_role || ''})` : 'Customer',
          `'${p.no_wa || ''}`,
          p.submitted_at ? formatDateExcel(p.submitted_at) : '-',
          p.confirmed_at ? formatDateExcel(p.confirmed_at) : '-',
          p.status === 'confirmed' ? 'Lunas' : p.status
        ]);
      } else {
        // Full mode - include admin columns with breakdown
        wsData.push([
          ...baseRow,
          formatRupiah(p.admin_fee),
          isPDAM ? formatRupiah(biayaPetugas) : '-',
          isPDAM ? formatRupiah(biayaSistem) : '-',
          isPDAM ? formatRupiah(biayaMaintenance) : '-',
          formatRupiah(p.total_bayar),
          p.payment_method?.toLowerCase() === 'cash' ? 'Cash' : p.payment_method?.toLowerCase() === 'transfer' ? 'Transfer' : '-',
          p.cashier_name ? `${p.cashier_name} (${p.cashier_role || ''})` : 'Customer',
          `'${p.no_wa || ''}`,
          p.submitted_at ? formatDateExcel(p.submitted_at) : '-',
          p.confirmed_at ? formatDateExcel(p.confirmed_at) : '-',
          p.status === 'confirmed' ? 'Lunas' : p.status
        ]);
      }
    });

    // Add total row
    wsData.push([]);
    if (isTagihanOnly) {
      wsData.push([
        "", "", "", "", "TOTAL:",
        formatRupiah(totalTagihan),
        "", "", "", "", ""
      ]);
    } else {
      wsData.push([
        "", "", "", "", "TOTAL:",
        formatRupiah(totalTagihan),
        formatRupiah(totalAdmin),
        formatRupiah(displaySummary.total_petugas || 0),
        formatRupiah(displaySummary.total_sistem || 0),
        formatRupiah(displaySummary.total_maintenance || 0),
        formatRupiah(totalBayar),
        "", "", "", "", ""
      ]);
    }

    // Add summary
    wsData.push([]);
    wsData.push(["RINGKASAN"]);
    wsData.push(["Total Transaksi:", displaySummary.total_transactions]);
    wsData.push(["Total Tagihan:", formatRupiah(displaySummary.total_tagihan || totalTagihan)]);
    if (!isTagihanOnly) {
      wsData.push(["Total Biaya Admin:", formatRupiah(totalAdmin)]);
      wsData.push([]);
      wsData.push(["BREAKDOWN ADMIN (Rp 2.500/bulan):"]);
      wsData.push(["- Petugas (Rp 1.000/bln):", formatRupiah(displaySummary.total_petugas || 0)]);
      wsData.push(["- Sistem (Rp 1.000/bln):", formatRupiah(displaySummary.total_sistem || 0)]);
      wsData.push(["- Maintenance (Rp 500/bln):", formatRupiah(displaySummary.total_maintenance || 0)]);
      wsData.push([]);
      wsData.push(["Total Pendapatan:", formatRupiah(totalBayar)]);
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths - different for each mode
    ws['!cols'] = isTagihanOnly 
      ? [
          { wch: 5 },   // No
          { wch: 14 },  // No Sambungan
          { wch: 8 },   // Bulan
          { wch: 25 },  // Nama
          { wch: 30 },  // Periode
          { wch: 18 },  // Tagihan
          { wch: 16 },  // Kasir
          { wch: 16 },  // No WA
          { wch: 18 },  // Tgl Submit
          { wch: 18 },  // Tgl Konfirmasi
          { wch: 10 },  // Status
        ]
      : [
          { wch: 5 },   // No
          { wch: 14 },  // No Sambungan
          { wch: 8 },   // Bulan
          { wch: 25 },  // Nama
          { wch: 30 },  // Periode
          { wch: 18 },  // Tagihan
          { wch: 15 },  // Biaya Admin
          { wch: 15 },  // Jatah Petugas
          { wch: 15 },  // Jatah Sistem
          { wch: 15 },  // Jatah Maint.
          { wch: 18 },  // Total Bayar
          { wch: 16 },  // Kasir
          { wch: 16 },  // No WA
          { wch: 18 },  // Tgl Submit
          { wch: 18 },  // Tgl Konfirmasi
          { wch: 10 },  // Status
        ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Laporan PDAM");

    // Generate file and download with mode indicator
    const modeFileSuffix = isTagihanOnly ? '_tagihan_saja' : '';
    XLSX.writeFile(wb, `laporan_pdam_${new Date().toISOString().split("T")[0]}${modeFileSuffix}.xlsx`);
    toast.success("File Excel berhasil di-download");
  };

  const formatDateExcel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Export PDF as image slides (25 rows per page)
  const exportPDF = async () => {
    if (!reports || !displayPayments.length) {
      toast.error("Tidak ada data untuk di-export");
      return;
    }

    setIsExportingPDF(true);
    const modeLabel = showMode === "tagihan_only" ? " (Tagihan Saja)" : "";
    toast.loading(`Memproses export PDF${modeLabel}...`, { id: "pdf-export" });

    try {
      const ROWS_PER_PAGE = 25;
      const payments = displayPayments;
      const totalPages = Math.ceil(payments.length / ROWS_PER_PAGE);
      const isTagihanOnly = showMode === "tagihan_only";
      
      // Create PDF (A4 landscape for better table fit)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Helper function to format currency
      const formatRp = (amount) => `Rp ${new Intl.NumberFormat("id-ID").format(amount || 0)}`;
      
      // Helper function to format date
      const formatTgl = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        });
      };

      // Count months helper - prefer jumlah_bulan from server
      const countMonths = (p) => {
        if (p.service_type !== 'pdam' && p.service_type !== 'pdam_tebo') return '-';
        // First try jumlah_bulan from server
        if (p.jumlah_bulan && p.jumlah_bulan > 0) {
          return p.jumlah_bulan;
        }
        // Fallback to parsing periode_list
        if (!p.periode_list) return 1;
        const months = p.periode_list.split(',').filter(m => m.trim()).length;
        return months || 1;
      };

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const startIdx = page * ROWS_PER_PAGE;
        const endIdx = Math.min(startIdx + ROWS_PER_PAGE, payments.length);
        const pagePayments = payments.slice(startIdx, endIdx);

        // Create temporary container for this page
        const container = document.createElement('div');
        container.style.cssText = `
          position: fixed;
          left: -9999px;
          top: 0;
          width: 1400px;
          padding: 24px;
          background: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;

        // Build page header with mode indicator
        const headerHtml = `
          <div style="margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="font-size: 20px; font-weight: 700; color: #0ea5e9; margin: 0;">
                LAPORAN PEMBAYARAN${isTagihanOnly ? ' <span style="font-size: 14px; background: #fed7aa; color: #c2410c; padding: 2px 10px; border-radius: 9999px; margin-left: 8px;">Tagihan Saja</span>' : ''}
              </h1>
              <p style="font-size: 12px; color: #64748b; margin: 4px 0 0 0;">
                Periode: ${startDate || 'Semua'} s/d ${endDate || 'Semua'} | Export: ${new Date().toLocaleDateString('id-ID')}
              </p>
            </div>
            <div style="text-align: right;">
              <span style="font-size: 14px; color: #64748b;">Halaman ${page + 1} dari ${totalPages}</span>
            </div>
          </div>
        `;

        // Build table rows - conditionally include admin columns
        let tableRows = '';
        pagePayments.forEach((p, idx) => {
          const rowNum = startIdx + idx + 1;
          const serviceLabel = p.service_type === 'pdam' ? 'PDAM' :
                              p.service_type === 'pdam_tebo' ? 'PDAM Tebo' :
                              p.service_type === 'token_listrik' ? 'Token' :
                              p.service_type === 'pulsa' ? 'Pulsa' : 'Lainnya';
          const serviceBg = p.service_type === 'pdam' ? '#dbeafe' :
                           p.service_type === 'pdam_tebo' ? '#cffafe' :
                           p.service_type === 'token_listrik' ? '#fef3c7' :
                           p.service_type === 'pulsa' ? '#f3e8ff' : '#f1f5f9';
          const serviceColor = p.service_type === 'pdam' ? '#1d4ed8' :
                              p.service_type === 'pdam_tebo' ? '#0891b2' :
                              p.service_type === 'token_listrik' ? '#d97706' :
                              p.service_type === 'pulsa' ? '#9333ea' : '#475569';
          
          const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
          
          // Admin columns only if not tagihan_only mode
          const adminCols = isTagihanOnly ? '' : `
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #059669;">${formatRp(p.admin_fee)}</td>
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; color: #0284c7;">${formatRp(p.total_bayar)}</td>
          `;
          
          tableRows += `
            <tr style="background: ${rowBg};">
              <td style="padding: 10px 8px; text-align: center; border-bottom: 1px solid #e2e8f0; font-size: 13px;">${rowNum}</td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0;">
                <span style="display: inline-block; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 600; background: ${serviceBg}; color: ${serviceColor};">
                  ${serviceLabel}
                </span>
              </td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-family: monospace; font-size: 13px;">${p.no_samb || '-'}</td>
              <td style="padding: 10px 8px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                ${(p.service_type === 'pdam' || p.service_type === 'pdam_tebo') ? 
                  `<span style="display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 9999px; background: #e0f2fe; color: #0284c7; font-weight: 600; font-size: 13px;">${countMonths(p)}</span>` 
                  : '<span style="color: #94a3b8;">-</span>'}
              </td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 13px; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${p.nama || p.product_name || '-'}</td>
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #ea580c;">${formatRp(p.jumlah)}</td>
              ${adminCols}
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px;">
                ${p.cashier_name ? 
                  `<span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; background: ${p.cashier_role === 'petugas' ? '#e0f2fe' : '#d1fae5'}; color: ${p.cashier_role === 'petugas' ? '#0284c7' : '#059669'};">${p.cashier_name}</span>` 
                  : '<span style="color: #94a3b8; font-size: 11px;">Customer</span>'}
              </td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px; color: #64748b;">${formatTgl(p.date)}</td>
            </tr>
          `;
        });

        // Build table header - conditionally include admin columns
        const adminHeaders = isTagihanOnly ? '' : `
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Admin</th>
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Bayar</th>
        `;

        // Build complete table HTML
        const tableHtml = `
          <table style="width: 100%; border-collapse: collapse; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
            <thead>
              <tr style="background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);">
                <th style="padding: 12px 8px; text-align: center; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">No</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Layanan</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">No. Pelanggan</th>
                <th style="padding: 12px 8px; text-align: center; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Bulan</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Nama</th>
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Tagihan</th>
                ${adminHeaders}
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Kasir</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Tanggal</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        `;

        // Add footer with totals on last page - conditionally show admin totals
        let footerHtml = '';
        if (page === totalPages - 1) {
          if (isTagihanOnly) {
            // Tagihan only mode - show only total tagihan
            footerHtml = `
              <div style="margin-top: 16px; padding: 16px; background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%); border-radius: 12px; display: flex; justify-content: flex-end; gap: 32px;">
                <div style="text-align: right;">
                  <span style="font-size: 12px; color: #9a3412;">Total Tagihan (Tanpa Admin)</span>
                  <p style="font-size: 20px; font-weight: 700; color: #c2410c; margin: 4px 0 0 0;">${formatRp(displaySummary.total_tagihan || 0)}</p>
                </div>
              </div>
            `;
          } else {
            // Full mode - show all totals with breakdown
            footerHtml = `
              <div style="margin-top: 16px; padding: 16px; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 12px;">
                <div style="display: flex; justify-content: flex-end; gap: 24px; margin-bottom: 12px;">
                  <div style="text-align: right;">
                    <span style="font-size: 12px; color: #64748b;">Total Tagihan</span>
                    <p style="font-size: 16px; font-weight: 700; color: #ea580c; margin: 4px 0 0 0;">${formatRp(displaySummary.total_tagihan || 0)}</p>
                  </div>
                  <div style="text-align: right;">
                    <span style="font-size: 12px; color: #64748b;">Total Admin</span>
                    <p style="font-size: 16px; font-weight: 700; color: #059669; margin: 4px 0 0 0;">${formatRp(displaySummary.total_admin_fee || 0)}</p>
                  </div>
                  <div style="text-align: right;">
                    <span style="font-size: 12px; color: #64748b;">Total Pendapatan</span>
                    <p style="font-size: 18px; font-weight: 700; color: #0284c7; margin: 4px 0 0 0;">${formatRp(displaySummary.total_amount)}</p>
                  </div>
                </div>
                <div style="border-top: 1px solid #e2e8f0; padding-top: 12px;">
                  <p style="font-size: 11px; color: #64748b; margin: 0 0 8px 0; font-weight: 600;">BREAKDOWN ADMIN (Rp 2.500/bulan):</p>
                  <div style="display: flex; justify-content: flex-end; gap: 24px;">
                    <div style="text-align: right;">
                      <span style="font-size: 11px; color: #7c3aed;">Petugas (Rp 1.000/bln)</span>
                      <p style="font-size: 14px; font-weight: 600; color: #7c3aed; margin: 2px 0 0 0;">${formatRp(displaySummary.total_petugas || 0)}</p>
                    </div>
                    <div style="text-align: right;">
                      <span style="font-size: 11px; color: #2563eb;">Sistem (Rp 1.000/bln)</span>
                      <p style="font-size: 14px; font-weight: 600; color: #2563eb; margin: 2px 0 0 0;">${formatRp(displaySummary.total_sistem || 0)}</p>
                    </div>
                    <div style="text-align: right;">
                      <span style="font-size: 11px; color: #d97706;">Maintenance (Rp 500/bln)</span>
                      <p style="font-size: 14px; font-weight: 600; color: #d97706; margin: 2px 0 0 0;">${formatRp(displaySummary.total_maintenance || 0)}</p>
                    </div>
                  </div>
                </div>
              </div>
            `;
          }
        }

        container.innerHTML = headerHtml + tableHtml + footerHtml;
        document.body.appendChild(container);

        // Convert to canvas then to image
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        // Remove temporary container
        document.body.removeChild(container);

        // Add image to PDF
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgWidth = pageWidth - 20; // 10mm margin on each side
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Center the image and fit to page
        const xPos = 10;
        const yPos = 10;
        const maxHeight = pageHeight - 20;
        
        if (imgHeight > maxHeight) {
          // Scale down if too tall
          const scaleFactor = maxHeight / imgHeight;
          pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth * scaleFactor, maxHeight);
        } else {
          pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth, imgHeight);
        }

        toast.loading(`Memproses halaman ${page + 1} dari ${totalPages}...`, { id: "pdf-export" });
      }

      // Save PDF with mode indicator in filename
      const modeFileSuffix = isTagihanOnly ? '_tagihan_saja' : '';
      const filename = `laporan_pembayaran_${startDate || 'all'}_${endDate || 'all'}${modeFileSuffix}.pdf`;
      pdf.save(filename);
      toast.success("PDF berhasil di-download!", { id: "pdf-export" });

    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error("Gagal export PDF", { id: "pdf-export" });
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <AdminLayout title="Laporan Pembayaran">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Filter */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Tanggal Mulai
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Tanggal Akhir
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11"
                  data-testid="end-date-input"
                />
              </div>
              <div className="relative" ref={userDropdownRef}>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Petugas/Agen
                </label>
                <button
                  type="button"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 text-left flex items-center justify-between"
                  data-testid="user-filter-select"
                >
                  <span className={selectedUsers.length === 0 ? "text-slate-500" : "text-slate-900"}>
                    {getSelectedUsersText()}
                  </span>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Multi-select dropdown */}
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {/* Select All / Clear */}
                    <div className="sticky top-0 bg-white border-b border-slate-100 p-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedUsers(users.map(u => u.id))}
                        className="text-xs text-sky-600 hover:text-sky-700 font-medium"
                      >
                        Pilih Semua
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={() => setSelectedUsers([])}
                        className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                      >
                        Hapus Semua
                      </button>
                    </div>
                    
                    {users.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span className="ml-2 text-sm text-slate-700">
                          {user.name || user.username}
                          <span className={`ml-1 text-xs ${user.role === 'petugas' ? 'text-sky-500' : 'text-emerald-500'}`}>
                            ({user.role})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  <Filter className="w-4 h-4 inline mr-1" />
                  Tampilkan
                </label>
                <select
                  value={showMode}
                  onChange={(e) => setShowMode(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="show-mode-select"
                >
                  <option value="all">Tagihan + Admin</option>
                  <option value="tagihan_only">Tagihan Saja</option>
                </select>
              </div>
              <Button onClick={fetchReports} className="h-11 px-6" data-testid="filter-btn">
                Filter
              </Button>
              <Button 
                variant="outline" 
                onClick={exportCSV} 
                className="h-11 px-6 gap-2"
                data-testid="export-excel-btn"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
            </div>
            <div className="mt-4 flex justify-end">
              <Button 
                variant="outline" 
                onClick={exportPDF}
                disabled={isExportingPDF}
                className="h-11 px-6 gap-2 bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                data-testid="export-pdf-btn"
              >
                <FileImage className="w-4 h-4" />
                {isExportingPDF ? 'Memproses...' : 'Export PDF'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        {reports && (
          <>
            {/* Main Summary Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Card className="stat-card">
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 mb-1">Total Transaksi</p>
                  <p className="text-2xl font-heading font-bold text-slate-800">
                    {displaySummary.total_transactions}
                  </p>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 mb-1">Total Tagihan</p>
                  <p className="text-xl font-heading font-bold text-orange-600">
                    {formatCurrency(displaySummary.total_tagihan || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Tanpa biaya admin</p>
                </CardContent>
              </Card>
              {showMode === "all" && (
                <Card className="stat-card">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-500 mb-1">Total Biaya Admin</p>
                    <p className="text-xl font-heading font-bold text-emerald-600">
                      {formatCurrency(displaySummary.total_admin_fee || 0)}
                    </p>
                  </CardContent>
                </Card>
              )}
              <Card className={`stat-card ${showMode === "tagihan_only" ? "lg:col-span-2" : ""}`}>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 mb-1">
                    {showMode === "tagihan_only" ? "Total Tagihan" : "Total Pendapatan"}
                  </p>
                  <p className="text-xl font-heading font-bold text-sky-600">
                    {showMode === "tagihan_only" 
                      ? formatCurrency(displaySummary.total_tagihan || 0)
                      : formatCurrency(displaySummary.total_amount)
                    }
                  </p>
                  {showMode === "all" && (
                    <p className="text-xs text-slate-400 mt-1">Tagihan + Admin</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Admin Fee Breakdown Row - Only show when showMode is "all" */}
            {showMode === "all" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Card className="stat-card border-l-4 border-l-violet-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Petugas</p>
                    <p className="text-lg font-heading font-bold text-violet-600">
                      {formatCurrency(displaySummary.total_petugas || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Rp 1.000 × jumlah bulan</p>
                  </CardContent>
                </Card>
                <Card className="stat-card border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Sistem</p>
                    <p className="text-lg font-heading font-bold text-blue-600">
                      {formatCurrency(displaySummary.total_sistem || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Rp 1.000 × jumlah bulan</p>
                  </CardContent>
                </Card>
                <Card className="stat-card border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Maintenance</p>
                    <p className="text-lg font-heading font-bold text-amber-600">
                      {formatCurrency(displaySummary.total_maintenance || 0)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Rp 500 × jumlah bulan</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Tab: DigiFlazz vs Lainnya */}
        {allPayments.length > 0 && (
          <div className="flex gap-2 mb-4" data-testid="report-tabs">
            {[["digiflazz", "DigiFlazz"], ["lainnya", "Lainnya"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                data-testid={`tab-${key}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === key
                    ? "bg-sky-600 text-white shadow"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Payments Table */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : reports && reports.message ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 text-sky-300 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                Pilih Rentang Tanggal
              </h3>
              <p className="text-slate-600">
                {reports.message}
              </p>
            </CardContent>
          </Card>
        ) : reports && displayPayments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                Tidak Ada Data
              </h3>
              <p className="text-slate-600">
                Tidak ada pembayaran yang dikonfirmasi pada periode ini
              </p>
            </CardContent>
          </Card>
        ) : reports && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Detail Pembayaran
                {showMode === "tagihan_only" && (
                  <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                    Mode: Tagihan Saja
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="table-wrapper">
                <table className="data-table" data-testid="reports-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Layanan</th>
                      <th>No. Pelanggan</th>
                      <th>Bulan</th>
                      <th>Nama</th>
                      <th>Total Tagihan</th>
                      {showMode === "all" && <th>Total Admin</th>}
                      {showMode === "all" && <th>Petugas</th>}
                      {showMode === "all" && <th>Sistem</th>}
                      {showMode === "all" && <th>Maint.</th>}
                      {showMode === "all" && <th>Total Bayar</th>}
                      <th>Metode</th>
                      <th>Kasir</th>
                      <th>Tanggal</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPayments.map((payment, index) => {
                      // Calculate total months - prefer server-side jumlah_bulan, then fall back to parsing periode_list
                      const countMonths = () => {
                        if (payment.service_type !== 'pdam' && payment.service_type !== 'pdam_tebo') return 0;
                        // First try to use jumlah_bulan from server (already calculated correctly for batch payments)
                        if (payment.jumlah_bulan && payment.jumlah_bulan > 0) {
                          return payment.jumlah_bulan;
                        }
                        // Fallback to parsing periode_list
                        if (!payment.periode_list) return 1;
                        const months = payment.periode_list.split(',').filter(m => m.trim()).length;
                        return months || 1;
                      };
                      
                      const bulanCount = countMonths();
                      const isPDAM = payment.service_type === 'pdam' || payment.service_type === 'pdam_tebo';
                      // Calculate breakdown per transaction
                      const biayaPetugas = isPDAM ? bulanCount * 1000 : 0;
                      const biayaSistem = isPDAM ? bulanCount * 1000 : 0;
                      const biayaMaintenance = isPDAM ? bulanCount * 500 : 0;
                      
                      return (
                        <tr key={payment.id}>
                          <td className="text-center">{index + 1}</td>
                          <td>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              payment.service_type === 'pdam' ? 'bg-blue-100 text-blue-700' :
                              payment.service_type === 'pdam_tebo' ? 'bg-cyan-100 text-cyan-700' :
                              payment.service_type === 'token_listrik' ? 'bg-yellow-100 text-yellow-700' :
                              payment.service_type === 'pulsa' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {payment.service_type === 'pdam' ? 'PDAM' :
                               payment.service_type === 'pdam_tebo' ? 'PDAM Tebo' :
                               payment.service_type === 'token_listrik' ? 'Token' :
                               payment.service_type === 'pulsa' ? 'Pulsa' : 
                               payment.product_name || 'Lainnya'}
                            </span>
                          </td>
                          <td className="font-mono">{payment.no_samb}</td>
                          <td className="text-center">
                            {isPDAM ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-100 text-sky-700 font-semibold text-sm">
                                {bulanCount}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td>{payment.nama || payment.product_name}</td>
                          <td className="text-right font-medium text-orange-600">{formatCurrency(payment.jumlah || 0)}</td>
                          {showMode === "all" && (
                            <td className="text-right font-medium text-emerald-600">{formatCurrency(payment.admin_fee || 0)}</td>
                          )}
                          {showMode === "all" && (
                            <td className="text-right text-sm text-violet-600">{isPDAM ? formatCurrency(biayaPetugas) : '-'}</td>
                          )}
                          {showMode === "all" && (
                            <td className="text-right text-sm text-blue-600">{isPDAM ? formatCurrency(biayaSistem) : '-'}</td>
                          )}
                          {showMode === "all" && (
                            <td className="text-right text-sm text-amber-600">{isPDAM ? formatCurrency(biayaMaintenance) : '-'}</td>
                          )}
                          {showMode === "all" && (
                            <td className="text-right font-medium text-sky-600">{formatCurrency(payment.total_bayar)}</td>
                          )}
                          <td className="text-center">
                            {payment.payment_method?.toLowerCase() === 'cash' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">Cash</span>
                            ) : payment.payment_method?.toLowerCase() === 'transfer' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Transfer</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td>
                            {payment.cashier_name ? (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${payment.cashier_role === 'petugas' ? 'bg-sky-100 text-sky-700' : payment.cashier_role === 'agen' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                {payment.cashier_name}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Customer</span>
                            )}
                          </td>
                          <td className="text-sm text-slate-500">
                            {payment.date ? formatDate(payment.date) : "-"}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setViewingPayment(payment)}
                              className="gap-1 text-sky-600 border-sky-200 hover:bg-sky-50"
                              data-testid={`detail-btn-${payment.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              Detail
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr>
                      <td colSpan="5" className="text-right">TOTAL:</td>
                      <td className="text-right text-orange-600">{formatCurrency(displaySummary.total_tagihan || 0)}</td>
                      {showMode === "all" && (
                        <td className="text-right text-emerald-600">{formatCurrency(displaySummary.total_admin_fee || 0)}</td>
                      )}
                      {showMode === "all" && (
                        <td className="text-right text-violet-600">{formatCurrency(displaySummary.total_petugas || 0)}</td>
                      )}
                      {showMode === "all" && (
                        <td className="text-right text-blue-600">{formatCurrency(displaySummary.total_sistem || 0)}</td>
                      )}
                      {showMode === "all" && (
                        <td className="text-right text-amber-600">{formatCurrency(displaySummary.total_maintenance || 0)}</td>
                      )}
                      {showMode === "all" && (
                        <td className="text-right text-sky-600">{formatCurrency(displaySummary.total_amount)}</td>
                      )}
                      <td colSpan={showMode === "all" ? "4" : "4"}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Detail Modal */}
        {viewingPayment && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingPayment(null)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative max-w-lg w-full bg-white rounded-2xl overflow-hidden" 
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-sky-50">
                <h3 className="font-heading font-semibold text-slate-800">Detail Transaksi</h3>
                <button
                  onClick={() => setViewingPayment(null)}
                  className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-slate-500 mb-3">Informasi Pelanggan</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">No. Sambungan</span>
                      <span className="font-mono font-medium">{viewingPayment.no_samb}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Nama</span>
                      <span className="font-medium">{viewingPayment.nama}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Periode</span>
                      <span className="font-medium">{viewingPayment.periode_list || viewingPayment.bulan}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">No. WhatsApp</span>
                      <span className="font-medium">{viewingPayment.no_wa || '-'}</span>
                    </div>
                    {viewingPayment.cashier_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Kasir</span>
                        <span className="font-semibold">{viewingPayment.cashier_name} ({viewingPayment.cashier_role})</span>
                      </div>
                    )}
                    {viewingPayment.source && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sumber</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${viewingPayment.source?.startsWith('user_') ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>
                          {viewingPayment.source?.startsWith('user_') ? 'Petugas/Agen' : 'Customer'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-emerald-700 mb-3">Informasi Pembayaran</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-600">Tagihan</span>
                      <span className="font-medium">{formatCurrency(viewingPayment.jumlah)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-600">Biaya Admin</span>
                      <span className="font-medium">{formatCurrency(viewingPayment.admin_fee || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-emerald-200">
                      <span className="text-emerald-700 font-semibold">Total Bayar</span>
                      <span className="font-bold text-emerald-700">{formatCurrency(viewingPayment.total_bayar)}</span>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-slate-500 text-xs mb-1">Tanggal Submit</p>
                    <p className="font-medium">{viewingPayment.submitted_at ? formatDate(viewingPayment.submitted_at) : '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-slate-500 text-xs mb-1">Tanggal Konfirmasi</p>
                    <p className="font-medium">{viewingPayment.confirmed_at ? formatDate(viewingPayment.confirmed_at) : '-'}</p>
                  </div>
                </div>

                {/* Payment Proof */}
                <div>
                  <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Bukti Pembayaran
                  </h4>
                  {viewingPayment.payment_proof ? (
                    <div 
                      className="cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setViewingProof(viewingPayment.payment_proof)}
                    >
                      <img 
                        src={viewingPayment.payment_proof} 
                        alt="Bukti Pembayaran" 
                        className="w-full h-48 object-cover rounded-xl border border-slate-200"
                      />
                      <p className="text-xs text-center text-slate-500 mt-2">Klik untuk memperbesar</p>
                    </div>
                  ) : (
                    <div className="bg-slate-100 rounded-xl p-6 text-center">
                      <Image className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Tidak ada bukti pembayaran</p>
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                <div className="flex justify-center">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    viewingPayment.status === 'confirmed' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {viewingPayment.status === 'confirmed' ? '✓ Transaksi Sukses' : 'Menunggu Konfirmasi'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Full Size Image Modal */}
        {viewingProof && (
          <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setViewingProof(null)}>
            <button
              onClick={() => setViewingProof(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img 
              src={viewingProof} 
              alt="Bukti Pembayaran" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
      </motion.div>
    </AdminLayout>
  );
}
